import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, Plus } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockItemListOptions } from "@shared-core/data/adapter";
import type {
  StockItemListItem,
  StockLocation,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { formatLocationBalances } from "@shared-core/domain/stock";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { useCanManageStock } from "../../app/auth/hooks";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Checkbox } from "../../app/components/primitives/checkbox";
import { Label } from "../../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { selectClassName } from "./StockFormField";
import { StockItemFormDialog } from "./StockItemFormDialog";
import { StockTabs } from "./StockTabs";

// Server-side search, filter, sort and paging (docs/list-endpoints.md):
// every change refetches and returns to page 1; rows render as returned.

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "stock_code", label: "Stock code" },
  { value: "description", label: "Description" },
  { value: "on_hand,stock_code", label: "Lowest on hand first" },
  { value: "-on_hand,stock_code", label: "Highest on hand first" },
  { value: "-updated_at", label: "Recently changed" },
];

export function StockItemsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const canManage = useCanManageStock();

  const [items, setItems] = useState<StockItemListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [needsReorderOnly, setNeedsReorderOnly] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [sort, setSort] = useState(SORT_OPTIONS[0]!.value);
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, needsReorderOnly, includeArchived, sort, locationId]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockLocations()
      .then((result) => !cancelled && setLocations(result))
      .catch((error: unknown) => {
        console.error("stock.locations.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    let cancelled = false;
    const options: StockItemListOptions = { page, pageSize: PAGE_SIZE, sort };
    if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
    if (needsReorderOnly) options.needsReorder = true;
    if (includeArchived) options.includeArchived = true;
    if (locationId) options.locationId = locationId;
    setLoadError(false);
    dataAdapter
      .listStockItems(options)
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.items.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setFirstLoad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    dataAdapter,
    page,
    debouncedSearch,
    needsReorderOnly,
    includeArchived,
    sort,
    locationId,
  ]);

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading stock…</div>
      </div>
    );
  }

  const isFiltered =
    Boolean(debouncedSearch.trim()) ||
    needsReorderOnly ||
    includeArchived ||
    Boolean(locationId);
  const filteredLocation = locations.find((l) => l.id === locationId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl">Stock</h1>
          <p className="text-sm text-muted-foreground">
            What's on hand in each warehouse, who owns the stock and when to
            re-order.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="size-4" />
              New stock item
            </Button>
          )}
        </div>
      </div>

      <StockTabs active="items" />

      <div className="flex flex-wrap items-end gap-4">
        <input
          type="search"
          aria-label="Search stock"
          placeholder="Search code or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-72"
        />
        <select
          aria-label="Warehouse"
          className={`${selectClassName} w-auto`}
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          <option value="">All warehouses</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Checkbox
            id="stock-needs-reorder"
            checked={needsReorderOnly}
            onCheckedChange={(checked) => setNeedsReorderOnly(checked === true)}
          />
          <Label htmlFor="stock-needs-reorder">Needs re-order</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="stock-include-archived"
            checked={includeArchived}
            onCheckedChange={(checked) => setIncludeArchived(checked === true)}
          />
          <Label htmlFor="stock-include-archived">Show archived</Label>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <Label htmlFor="stock-sort" className="whitespace-nowrap">
            Sort by
          </Label>
          <select
            id="stock-sort"
            className={`${selectClassName} w-auto`}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load stock. Please try again.
        </div>
      )}

      {!loadError && items.length === 0 ? (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          {isFiltered
            ? "No stock items match these filters."
            : canManage
              ? "No stock items yet. Use New stock item to add the first one."
              : "No stock items yet."}
        </div>
      ) : (
        !loadError && (
          <>
            <div className="text-sm text-muted-foreground">
              Showing {formatCount((page - 1) * PAGE_SIZE + 1)}–
              {formatCount(Math.min(page * PAGE_SIZE, total))} of{" "}
              {formatCount(total)} stock items
            </div>
            <div className="rounded-lg border border-border bg-surface overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stock code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Warehouses</TableHead>
                    {filteredLocation && (
                      <TableHead className="text-right">
                        At {filteredLocation.name}
                      </TableHead>
                    )}
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">Projected</TableHead>
                    <TableHead className="text-right">Re-order at</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(
                          paths.stockItemDetail({ stockItemId: item.id }),
                        )
                      }
                    >
                      <TableCell className="font-medium">
                        <Link
                          to={paths.stockItemDetail({ stockItemId: item.id })}
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.stock_code}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div>{item.description}</div>
                        {item.size && (
                          <div className="text-xs text-muted-foreground">
                            {item.size}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.location_balances.length > 0
                          ? formatLocationBalances(item.location_balances)
                          : "–"}
                      </TableCell>
                      {filteredLocation && (
                        <TableCell className="text-right tabular-nums">
                          {formatCount(item.location_on_hand ?? 0)}
                        </TableCell>
                      )}
                      <TableCell className="text-right tabular-nums">
                        {formatCount(item.levels.on_hand)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(item.levels.projected)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {item.reorder_level == null
                          ? "–"
                          : formatCount(item.reorder_level)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.owner_name ?? (
                          <span className="text-muted-foreground">
                            Owner no longer active
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {item.is_archived && (
                            <Badge variant="neutral">Archived</Badge>
                          )}
                          {item.levels.needs_reorder && !item.is_archived && (
                            <Badge variant="warning">Needs re-order</Badge>
                          )}
                          {item.tracks_batches && (
                            <Badge variant="neutral">Batches</Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ListPagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )
      )}

      {isCreateOpen && (
        <StockItemFormDialog
          onClose={() => setIsCreateOpen(false)}
          onSaved={(created) =>
            navigate(paths.stockItemDetail({ stockItemId: created.id }))
          }
        />
      )}
    </div>
  );
}
